import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { SERVICES as FALLBACK_SERVICES } from "../data/services";
import AuthModal from "./AuthModal";
import QuoteModal from "./QuoteModal";
import {
  ChevronDown, ChevronRight, Save, Download, Send, FileText,
  Plus, Minus, RefreshCw, Hammer, Trash2, Package, Wrench, BarChart2,
  Check, X as XIcon
} from "lucide-react";

import { API } from "../config";

// Single service item row
function ServiceRow({ item, lang, onUpdate, currentQty }) {
  const name = item.name[lang] || item.name.lv;
  const qty = currentQty || 0;

  const handleChange = (e) => {
    const val = Math.max(0, parseInt(e.target.value) || 0);
    onUpdate(item.id, val, item.price, name, item.unit);
  };

  return (
    <div
      data-testid={`service-row-${item.id}`}
      className={`service-row ${qty > 0 ? "active" : ""}`}
    >
      <input
        type="number"
        min="0"
        step="1"
        value={qty || ""}
        onChange={handleChange}
        placeholder="0"
        className="qty-input"
        data-testid={`qty-input-${item.id}`}
        title={`${name} daudzums`}
      />
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-800 block truncate">{name}</span>
        <span className="text-xs text-gray-400">
          €{item.price_min}–{item.price_max}/{item.unit}
        </span>
      </div>
      <div className="text-right flex-shrink-0">
        <span className="text-xs text-gray-400 block">{item.unit}</span>
        {qty > 0 && (
          <span className="text-sm font-black text-[#FF6600]" style={{ fontFamily: "Chivo, sans-serif" }}>
            €{(qty * item.price).toFixed(2)}
          </span>
        )}
      </div>
    </div>
  );
}

// Accordion group
function ServiceGroup({ group, lang, selectedItems, onUpdate, initOpen }) {
  const [open, setOpen] = useState(!!initOpen);
  const groupName = group.name[lang] || group.name.lv;
  const activeCount = group.items.filter((i) => (selectedItems[i.id]?.quantity || 0) > 0).length;

  return (
    <div className="border-b border-gray-100">
      <button
        data-testid={`group-toggle-${group.id}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className="font-bold text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>
            {groupName}
          </span>
          {activeCount > 0 && (
            <span className="bg-[#FF6600] text-black text-xs font-black px-1.5 py-0.5 leading-none">
              {activeCount}
            </span>
          )}
        </div>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {open && (
        <div className="bg-gray-50/30">
          {group.items.map((item) => (
            <ServiceRow
              key={item.id}
              item={item}
              lang={lang}
              currentQty={selectedItems[item.id]?.quantity}
              onUpdate={onUpdate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Material card for WP materials tab
function MaterialCard({ mat, lang, selectedItems, selectedStore, onUpdate, onStoreChange }) {
  const qty = selectedItems[mat.id]?.quantity || 0;
  const cheapest = mat.prices?.reduce((a, b) => (a.price < b.price ? a : b), mat.prices?.[0]);
  const chosen = selectedStore || cheapest?.store;
  const chosenPrice = mat.prices?.find((p) => p.store === chosen)?.price || cheapest?.price || 0;

  const handleQty = (val) => {
    const v = Math.max(0, parseInt(val) || 0);
    onUpdate(mat.id, v, chosenPrice, mat.name, mat.unit, "materials");
  };

  return (
    <div
      data-testid={`material-card-${mat.id}`}
      className={`border-2 p-4 transition-all ${qty > 0 ? "border-[#FF6600] bg-orange-50" : "border-black"}`}
      style={qty > 0 ? { boxShadow: "3px 3px 0 #FF6600" } : { boxShadow: "2px 2px 0 #000" }}
    >
      <div className="font-bold text-sm mb-3 leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
        {mat.name}
      </div>

      {/* Store badges */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {mat.prices?.map((p) => (
          <button
            key={p.store}
            data-testid={`store-btn-${mat.id}-${p.store}`}
            onClick={() => {
              onStoreChange(mat.id, p.store);
              if (qty > 0) onUpdate(mat.id, qty, p.price, mat.name, mat.unit, "materials");
            }}
            className="store-badge"
            style={{
              borderColor: chosen === p.store ? "#FF6600" : "#ccc",
              background: chosen === p.store ? "#FF6600" : "transparent",
              color: chosen === p.store ? "#000" : "#666",
            }}
          >
            {p.store}: €{p.price}
          </button>
        ))}
      </div>

      {/* Quantity input */}
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0"
          step="1"
          value={qty || ""}
          onChange={(e) => handleQty(e.target.value)}
          placeholder="0"
          className="qty-input flex-1"
          data-testid={`mat-qty-${mat.id}`}
        />
        <span className="text-xs text-gray-500 w-12 text-right">{mat.unit}</span>
        {qty > 0 && (
          <span className="text-sm font-black text-[#FF6600]" style={{ fontFamily: "Chivo, sans-serif" }}>
            €{(qty * chosenPrice).toFixed(2)}
          </span>
        )}
      </div>
    </div>
  );
}

// ---- Comparison Tab ----
const CMP_VARIANTS = [
  {
    id: "quick", color: "#22c55e", textColor: "white", pricePerM2: 25, timelineM2: 0.07, minWeeks: 1,
    badge: { lv: "EKONOMISKS SĀKUMS", ru: "БЮДЖЕТНЫЙ СТАРТ", en: "BUDGET START" },
    label: { lv: "ĀTRAIS", ru: "БЫСТРЫЙ", en: "QUICK" },
    price_range: { lv: "€15–35/m²", ru: "€15–35/м²", en: "€15–35/m²" },
    desc: { lv: "Krāsošana, jauna grīda, tīrīšana.", ru: "Покраска, новый пол, уборка.", en: "Painting, new floor, cleaning." },
    includes: [
      { lv: "Tapešu noņemšana", ru: "Снятие обоев", en: "Wallpaper removal", ok: true },
      { lv: "Sienu krāsošana (2 kārtas)", ru: "Покраска стен (2 слоя)", en: "Wall painting (2 coats)", ok: true },
      { lv: "Griestu krāsošana", ru: "Покраска потолка", en: "Ceiling painting", ok: true },
      { lv: "Jauns grīdas segums", ru: "Новый пол (ламинат)", en: "New floor covering", ok: true },
      { lv: "Gala tīrīšana", ru: "Финальная уборка", en: "Final cleaning", ok: true },
      { lv: "Sienu špaktelēšana", ru: "Шпаклёвка стен", en: "Wall plastering", ok: false },
      { lv: "Elektrikas atjaunošana", ru: "Электрика", en: "Electrical works", ok: false },
      { lv: "Santehnika", ru: "Сантехника", en: "Plumbing", ok: false },
      { lv: "Flīzēšana", ru: "Укладка плитки", en: "Tiling", ok: false },
    ],
  },
  {
    id: "economy", color: "#FF6600", textColor: "black", pricePerM2: 100, timelineM2: 0.18, minWeeks: 4,
    badge: { lv: "POPULĀRĀKAIS", ru: "ПОПУЛЯРНЫЙ", en: "MOST POPULAR" },
    label: { lv: "EKONOMISKAIS", ru: "ЭКОНОМНЫЙ", en: "ECONOMY" },
    price_range: { lv: "€80–130/m²", ru: "€80–130/м²", en: "€80–130/m²" },
    desc: { lv: "Pilnīga apdare – špaktelēšana, krāsošana, grīdas, pamata elektrika.", ru: "Полная отделка — шпаклёвка, покраска, полы, базовая электрика.", en: "Full finish — plastering, painting, flooring, basic electrical." },
    includes: [
      { lv: "Visi demolēšanas darbi", ru: "Все демонтажные работы", en: "All demolition works", ok: true },
      { lv: "Sienu/griestu špaktelēšana", ru: "Шпаклёвка стен/потолка", en: "Wall/ceiling plastering", ok: true },
      { lv: "Krāsošana (sienas + griesti)", ru: "Покраска (стены + потолок)", en: "Painting (walls + ceiling)", ok: true },
      { lv: "Jauns grīdas segums visur", ru: "Новый пол везде", en: "New floor everywhere", ok: true },
      { lv: "Flīzēšana vannas istabā", ru: "Укладка плитки в ванной", en: "Bathroom tiling", ok: true },
      { lv: "Pamata elektrika", ru: "Базовая электрика", en: "Basic electrical", ok: true },
      { lv: "Pamata santehnika", ru: "Базовая сантехника", en: "Basic plumbing", ok: true },
      { lv: "Ģipškartons griestiem", ru: "Гипсокартон", en: "Drywall ceiling", ok: false },
      { lv: "Siltā grīda", ru: "Тёплый пол", en: "Underfloor heating", ok: false },
    ],
  },
  {
    id: "premium", color: "#111111", textColor: "white", pricePerM2: 250, timelineM2: 0.3, minWeeks: 8,
    badge: { lv: "PILNĪGA RENOVĀCIJA", ru: "ПОЛНАЯ РЕНОВАЦИЯ", en: "FULL RENOVATION" },
    label: { lv: "PREMIUM", ru: "ПРЕМИУМ", en: "PREMIUM" },
    price_range: { lv: "€200–320/m²", ru: "€200–320/м²", en: "€200–320/m²" },
    desc: { lv: "Pilna renovācija – elektrika, santehnika, ģipškartons, siltā grīda, premium apdare.", ru: "Полная реновация — электрика, сантехника, гипсокартон, тёплый пол, отделка премиум.", en: "Full renovation — electrical, plumbing, drywall, underfloor heating, premium finish." },
    includes: [
      { lv: "Visi demolēšanas darbi", ru: "Все демонтажные работы", en: "All demolition works", ok: true },
      { lv: "Pilnīga elektrikas nomaiņa", ru: "Полная замена электрики", en: "Full electrical replacement", ok: true },
      { lv: "Pilnīga santehnikas nomaiņa", ru: "Полная замена сантехники", en: "Full plumbing replacement", ok: true },
      { lv: "Siltā grīda", ru: "Тёплый пол", en: "Underfloor heating", ok: true },
      { lv: "Ģipškartons sienas + griesti", ru: "Гипсокартон стены + потолок", en: "Drywall walls + ceiling", ok: true },
      { lv: "Pilnīga špaktelēšana + krāsošana", ru: "Полная шпаклёвка + покраска", en: "Full plastering + painting", ok: true },
      { lv: "Premium flīzēšana", ru: "Плитка премиум", en: "Premium tiling", ok: true },
      { lv: "Iekšdurvis + grīdlīstes", ru: "Двери + плинтусы", en: "Doors + skirting boards", ok: true },
      { lv: "Sanitārtehnika + fiksieri", ru: "Сантехника + светильники", en: "Sanitary fixtures + lighting", ok: true },
    ],
  },
];

function ComparisonTab({ area, setArea, lang, onQuote }) {
  const lbl = (o) => o[lang] || o.lv;
  const weeksLabel = lang === "ru" ? "нед." : lang === "en" ? "wks" : "ned.";
  const areaLabel = lang === "ru" ? "Площадь квартиры (м²)" : lang === "en" ? "Apartment area (m²)" : "Dzīvokļa platība (m²)";
  const vatLabel = lang === "ru" ? "* НДС 21% не включён" : lang === "en" ? "* VAT 21% not included" : "* Nav iekļauts PVN 21%";

  return (
    <div className="p-5" data-testid="comparison-tab-panel">
      {/* Area input */}
      <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 border border-gray-200">
        <label className="rmd-label whitespace-nowrap">{areaLabel}:</label>
        <input type="range" min="20" max="300" step="5" value={area}
          onChange={e => setArea(parseInt(e.target.value))}
          className="flex-1" style={{ accentColor: "#FF6600" }} />
        <input type="number" min="20" max="500" value={area}
          onChange={e => setArea(Math.max(20, parseInt(e.target.value) || area))}
          className="border-2 border-black px-2 py-1 text-sm font-bold w-16 text-center focus:outline-none focus:border-[#FF6600]" />
        <span className="text-sm font-semibold">m²</span>
      </div>

      {/* Variant cards */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {CMP_VARIANTS.map(v => {
          const price = Math.round(v.pricePerM2 * area);
          const weeks = Math.max(v.minWeeks, Math.ceil(v.timelineM2 * area));
          return (
            <div key={v.id} data-testid={`cmp-card-${v.id}`}
              className={`border-2 flex flex-col ${v.id === "economy" ? "border-[#FF6600]" : "border-black"}`}
              style={{ boxShadow: v.id === "economy" ? "4px 4px 0 #FF6600" : "3px 3px 0 #000" }}>
              {/* Header */}
              <div className="px-4 py-4" style={{ background: v.color, color: v.textColor }}>
                <div className="text-xs font-black uppercase tracking-widest opacity-80 mb-1">{lbl(v.badge)}</div>
                <div className="text-2xl font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{lbl(v.label)}</div>
                <div className="text-xs mt-1 opacity-80">{lbl(v.price_range)}</div>
                <div className="text-xs mt-1 opacity-60 leading-snug">{lbl(v.desc)}</div>
              </div>
              {/* Price */}
              <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-baseline bg-white">
                <div>
                  <div className="rmd-label mb-0.5">{lang === "ru" ? "Стоимость" : lang === "en" ? "Cost" : "Izmaksa"}</div>
                  <div className="text-xl font-black" style={{ fontFamily: "Chivo, sans-serif", color: v.id === "economy" ? "#FF6600" : "#000" }}>
                    ~€{price.toLocaleString()}
                  </div>
                </div>
                <div className="text-right">
                  <div className="rmd-label mb-0.5">{lang === "ru" ? "Время" : lang === "en" ? "Time" : "Laiks"}</div>
                  <div className="text-lg font-black" style={{ fontFamily: "Chivo, sans-serif" }}>~{weeks} {weeksLabel}</div>
                </div>
              </div>
              {/* Includes */}
              <div className="px-4 py-3 flex-1 bg-white">
                <ul className="space-y-1.5">
                  {v.includes.map((item, i) => (
                    <li key={i} className={`flex items-start gap-2 text-xs ${item.ok ? "text-gray-800" : "text-gray-300"}`}>
                      {item.ok
                        ? <Check size={12} className="flex-shrink-0 mt-0.5" color={v.id === "economy" ? "#FF6600" : v.id === "quick" ? "#22c55e" : "#111"} />
                        : <XIcon size={12} className="flex-shrink-0 mt-0.5" color="#d1d5db" />}
                      <span>{lbl(item)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* VAT note */}
              <div className="px-4 pb-2 bg-white">
                <p className="text-xs text-gray-400">{vatLabel}</p>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-400 mt-4 border-l-4 border-[#FF6600] pl-3">
        {lang === "ru"
          ? "Все расчёты ориентировочные. Точная стоимость определяется после осмотра объекта. Первый осмотр — бесплатно."
          : lang === "en"
          ? "All estimates are indicative. Exact cost determined after site inspection. First inspection — free."
          : "Visi aprēķini ir orientējoši. Precīza izmaksa tiek noteikta pēc objekta apskates. Pirmā apskate – bezmaksas."}
      </p>
    </div>
  );
}

// Tab categories
const TABS = [
  { id: "construction", icon: Hammer, dataKey: "construction" },
  { id: "demolition", icon: Trash2, dataKey: "demolition" },
  { id: "rental", icon: Wrench, dataKey: "rental" },
  { id: "materials", icon: Package, dataKey: null },
  { id: "comparison", icon: BarChart2, dataKey: null },
];

export default function CalculatorPage() {
  const { t, lang, tObj } = useLanguage();
  const { user, authHeaders } = useAuth();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("construction");
  const [selectedItems, setSelectedItems] = useState({});
  const [selectedStores, setSelectedStores] = useState({});
  const [wpMaterials, setWpMaterials] = useState([]);
  const [wpLoading, setWpLoading] = useState(false);
  const [services, setServices] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [apartmentArea, setApartmentArea] = useState(50);
  const [showAuth, setShowAuth] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [customItem, setCustomItem] = useState({ name: "", qty: "", price: "" });
  const [showCustom, setShowCustom] = useState(false);

  // Fetch services from WordPress admin
  useEffect(() => {
    axios.get(`${API}/rmd/v1/services`)
      .then(r => {
        if (r.data && r.data.length > 0) {
          // Build structure from API data
          const grouped = { construction: { subgroups: [] }, demolition: { subgroups: [] }, rental: { subgroups: [] } };
          const subgroupMap = {};
          
          r.data.forEach(svc => {
            const cat = svc.category;
            const sub = svc.subgroup;
            const sgKey = `${cat}_${sub}`;
            
            if (!subgroupMap[sgKey]) {
              subgroupMap[sgKey] = { id: sub, items: [], subgroupName: sub };
              if (grouped[cat]) grouped[cat].subgroups.push(subgroupMap[sgKey]);
            }
            
            subgroupMap[sgKey].items.push({
              id: svc.service_id,
              name: { lv: svc.name_lv, ru: svc.name_ru, en: svc.name_en },
              unit: svc.unit,
              price: parseFloat(svc.price),
              price_min: parseFloat(svc.price_min),
              price_max: parseFloat(svc.price_max),
            });
          });
          
          setServices(grouped);
        } else {
          setServices(FALLBACK_SERVICES);
        }
      })
      .catch(() => setServices(FALLBACK_SERVICES));
  }, []);

  // Load project from navigation state (My Projects → Load)
  useEffect(() => {
    const proj = location.state?.loadProject;
    if (proj) {
      setProjectName(proj.name || "");
      if (proj.items && Array.isArray(proj.items)) {
        const items = {};
        proj.items.forEach(item => {
          const id = item.id || `loaded_${Date.now()}_${Math.random()}`;
          items[id] = {
            quantity: item.quantity || 0,
            price: item.price || 0,
            name: item.name || "",
            unit: item.unit || "",
            category: item.category || "construction",
          };
        });
        setSelectedItems(items);
      }
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  // Fetch WP materials from admin system
  useEffect(() => {
    if (activeTab === "materials" && wpMaterials.length === 0) {
      setWpLoading(true);
      axios.get(`${API}/rmd/v1/materials`)
        .then((r) => setWpMaterials(r.data || []))
        .catch(() => setWpMaterials([]))
        .finally(() => setWpLoading(false));
    }
  }, [activeTab]);

  const updateItem = useCallback((id, quantity, price, name, unit, category = "construction") => {
    if (quantity <= 0) {
      setSelectedItems((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
    } else {
      setSelectedItems((prev) => ({
        ...prev,
        [id]: { quantity, price, name, unit, category },
      }));
    }
  }, []);

  const clearAll = () => {
    setSelectedItems({});
    setSelectedStores({});
  };

  // Computed total
  const total = useMemo(
    () => Object.values(selectedItems).reduce((s, i) => s + i.quantity * i.price, 0),
    [selectedItems]
  );

  // Items list for quote/save
  const itemsList = useMemo(
    () => Object.entries(selectedItems).map(([id, item]) => ({ id, ...item })),
    [selectedItems]
  );

  // Breakdown by category
  const breakdown = useMemo(() => {
    const groups = {};
    Object.values(selectedItems).forEach((item) => {
      if (!groups[item.category]) groups[item.category] = 0;
      groups[item.category] += item.quantity * item.price;
    });
    return groups;
  }, [selectedItems]);

  // Timeline estimate
  const estimatedWeeks = useMemo(() => {
    if (total <= 0) return 0;
    let days = 0;
    const cats = Object.keys(breakdown);
    if (cats.includes("demolition")) days += Math.max(2, apartmentArea * 0.08);
    if (cats.includes("construction")) days += Math.max(5, apartmentArea * 0.2);
    if (cats.includes("rental")) days += 3;
    if (cats.includes("materials")) days += 1;
    if (days === 0) days = apartmentArea * 0.15;
    return Math.max(1, Math.ceil(days / 5));
  }, [breakdown, apartmentArea, total]);

  const catLabel = (cat) => {
    const map = {
      construction: t("calculator.tabs.construction"),
      demolition: t("calculator.tabs.demolition"),
      rental: t("calculator.tabs.rental"),
      materials: t("calculator.tabs.materials"),
    };
    return map[cat] || cat;
  };

  const saveProject = async () => {
    if (!user) { setShowAuth(true); return; }
    if (itemsList.length === 0) return;
    try {
      await axios.post(`${API}/projects`, {
        name: projectName || "Mans projekts",
        items: itemsList,
        total_cost: total,
        notes: "",
      }, { headers: authHeaders });
      setSaveMsg("Projekts saglabāts!");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch {
      setSaveMsg("Kļūda saglabājot");
      setTimeout(() => setSaveMsg(""), 3000);
    }
  };

  const addCustom = () => {
    if (!customItem.name || !customItem.qty || !customItem.price) return;
    const id = `custom_${Date.now()}`;
    updateItem(id, parseInt(customItem.qty) || 1, parseFloat(customItem.price), customItem.name, "gab.", "construction");
    setCustomItem({ name: "", qty: "", price: "" });
  };

  const currentService = services?.[activeTab === "materials" ? "construction" : activeTab] || (FALLBACK_SERVICES ? FALLBACK_SERVICES[activeTab === "materials" ? "construction" : activeTab] : null);

  const tabLabels = {
    construction: t("calculator.tabs.construction"),
    demolition: t("calculator.tabs.demolition"),
    rental: t("calculator.tabs.rental"),
    materials: t("calculator.tabs.materials"),
    comparison: lang === "ru" ? "Сравнение" : lang === "en" ? "Comparison" : "Salīdzinājums",
  };

  return (
    <div style={{ paddingTop: 72 }} className="min-h-screen bg-[#f9f9f9]">
      {/* Header */}
      <section className="bg-black py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <span className="rmd-label text-[#FF6600] block mb-2">{t("calculator.title")}</span>
          <h1 className="text-white text-4xl md:text-6xl font-black uppercase tracking-tight leading-none" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="calculator-title">
            {t("calculator.title")}
          </h1>
          <p className="text-white/50 mt-3 text-sm">{t("calculator.subtitle")}</p>
        </div>
      </section>

      {/* Tabs */}
      <div className="border-b-2 border-black bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 md:px-12 flex overflow-x-auto">
          {TABS.map(({ id, icon: Icon }) => (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setActiveTab(id)}
              className={`tab-btn flex items-center gap-2 ${activeTab === id ? "active" : ""}`}
            >
              <Icon size={15} />
              {tabLabels[id]}
              {id !== "materials" && Object.values(selectedItems).filter(i => i.category === id).length > 0 && (
                <span className="bg-white text-black text-xs font-black px-1.5 py-0.5 rounded-sm leading-none min-w-[18px] text-center">
                  {Object.values(selectedItems).filter(i => i.category === id).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main panel */}
          <div className="flex-1 min-w-0">
            <div className="bg-white border-2 border-black" style={{ boxShadow: "4px 4px 0 #000" }}>
              {/* Tab content header */}
              <div className="px-6 py-4 border-b-2 border-black flex items-center justify-between">
                <h2 className="font-black text-lg uppercase tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
                  {tabLabels[activeTab]}
                </h2>
                <button
                  data-testid="clear-all-btn"
                  onClick={clearAll}
                  className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors"
                >
                  <RefreshCw size={12} /> Notīrīt visu
                </button>
              </div>

              {/* Construction / Demolition / Rental tabs */}
              {activeTab !== "materials" && currentService && (
                <div>
                  {(services?.[activeTab]?.subgroups || FALLBACK_SERVICES?.[activeTab]?.subgroups || [])?.map((group, i) => (
                    <ServiceGroup
                      key={group.id}
                      group={group}
                      lang={lang}
                      selectedItems={selectedItems}
                      onUpdate={(id, qty, price, name, unit) => updateItem(id, qty, price, name, unit, activeTab)}
                      initOpen={i === 0}
                    />
                  ))}

                  {/* Custom item */}
                  <div className="border-t-2 border-dashed border-gray-200 p-4">
                    <button
                      data-testid="custom-item-toggle"
                      onClick={() => setShowCustom((v) => !v)}
                      className="text-sm text-gray-500 hover:text-black flex items-center gap-2 transition-colors"
                    >
                      <Plus size={14} /> Pievienot pielāgotu pozīciju
                    </button>
                    {showCustom && (
                      <div className="mt-3 flex flex-wrap gap-2 items-end">
                        <div>
                          <label className="rmd-label block mb-1">Nosaukums</label>
                          <input
                            data-testid="custom-name-input"
                            value={customItem.name}
                            onChange={(e) => setCustomItem((c) => ({ ...c, name: e.target.value }))}
                            placeholder="Pielāgots darbs"
                            className="border-2 border-black px-3 py-1.5 text-sm w-44 focus:outline-none focus:border-[#FF6600]"
                          />
                        </div>
                        <div>
                          <label className="rmd-label block mb-1">Skaits</label>
                          <input
                            data-testid="custom-qty-input"
                            type="number"
                            min="0"
                            value={customItem.qty}
                            onChange={(e) => setCustomItem((c) => ({ ...c, qty: e.target.value }))}
                            placeholder="1"
                            className="border-2 border-black px-3 py-1.5 text-sm w-20 focus:outline-none focus:border-[#FF6600]"
                          />
                        </div>
                        <div>
                          <label className="rmd-label block mb-1">Cena/v. €</label>
                          <input
                            data-testid="custom-price-input"
                            type="number"
                            min="0"
                            value={customItem.price}
                            onChange={(e) => setCustomItem((c) => ({ ...c, price: e.target.value }))}
                            placeholder="50"
                            className="border-2 border-black px-3 py-1.5 text-sm w-24 focus:outline-none focus:border-[#FF6600]"
                          />
                        </div>
                        <button
                          data-testid="custom-add-btn"
                          onClick={addCustom}
                          className="rmd-btn-primary text-xs py-2 px-4"
                        >
                          <Plus size={14} /> Pievienot
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Materials tab */}
              {activeTab === "materials" && (
                <div className="p-6">
                  <p className="text-sm text-gray-500 mb-6 border-l-4 border-[#FF6600] pl-3">
                    Cenas iegūtas no jūsu WordPress veikala (realmendo.com). Izvēlieties veikalu ar izdevīgāko cenu.
                  </p>
                  {wpLoading ? (
                    <div className="text-center py-16">
                      <RefreshCw size={24} className="animate-spin mx-auto mb-3 text-[#FF6600]" />
                      <p className="text-gray-500 text-sm">Ielādē materiālus...</p>
                    </div>
                  ) : wpMaterials.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                      <Package size={40} className="mx-auto mb-3 opacity-30" />
                      <p>Nav pieejamu materiālu</p>
                      <p className="text-xs mt-2">Pievienojiet produktus WordPress administrācijā</p>
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {wpMaterials.map((mat) => (
                        <MaterialCard
                          key={mat.id}
                          mat={mat}
                          lang={lang}
                          selectedItems={selectedItems}
                          selectedStore={selectedStores[mat.id]}
                          onUpdate={(id, qty, price, name, unit, cat) => updateItem(id, qty, price, name, unit, cat)}
                          onStoreChange={(id, store) => setSelectedStores((s) => ({ ...s, [id]: store }))}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Comparison tab */}
              {activeTab === "comparison" && (
                <ComparisonTab area={apartmentArea} setArea={setApartmentArea} lang={lang} onQuote={(items, total, name) => { setShowQuote(true); }} />
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-80 xl:w-96 flex-shrink-0">
            <div
              className="bg-white border-2 border-black lg:sticky lg:top-20"
              style={{ boxShadow: "4px 4px 0 #000" }}
              data-testid="calculator-sidebar"
            >
              {/* Sidebar header */}
              <div className="bg-[#FF6600] border-b-2 border-black px-5 py-4">
                <h3 className="font-black text-lg uppercase tracking-tight text-black" style={{ fontFamily: "Chivo, sans-serif" }}>
                  {t("calculator.sidebar.title")}
                </h3>
              </div>

              <div className="p-5 space-y-5">
                {/* Project name */}
                <div>
                  <label className="rmd-label block mb-1">{t("calculator.sidebar.projectName")}</label>
                  <input
                    data-testid="project-name-input"
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder={t("calculator.sidebar.projectNamePlaceholder")}
                    className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                  />
                </div>

                {/* Apartment area */}
                <div>
                  <label className="rmd-label block mb-1">{t("calculator.sidebar.areaTitle")}</label>
                  <input
                    data-testid="apartment-area-input"
                    type="number"
                    min="5"
                    max="1000"
                    value={apartmentArea}
                    onChange={(e) => setApartmentArea(parseInt(e.target.value) || 50)}
                    className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                  />
                </div>

                {/* Cost breakdown */}
                <div>
                  {Object.keys(breakdown).length === 0 ? (
                    <div className="py-6 text-center text-gray-400 text-sm border-2 border-dashed border-gray-200">
                      {t("calculator.sidebar.noItems")}
                    </div>
                  ) : (
                    <div className="space-y-2 border-t border-gray-100 pt-3">
                      {Object.entries(breakdown).map(([cat, amt]) => (
                        <div key={cat} className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">{catLabel(cat)}</span>
                          <span className="font-semibold">€{amt.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Total */}
                <div className="border-t-2 border-black pt-4">
                  <div className="flex justify-between items-baseline">
                    <span className="font-black text-base uppercase tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
                      {t("calculator.sidebar.total")}
                    </span>
                    <span
                      className="font-black text-3xl text-[#FF6600]"
                      style={{ fontFamily: "Chivo, sans-serif" }}
                      data-testid="total-cost"
                    >
                      €{total.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{t("calculator.sidebar.vat")}</p>
                </div>

                {/* Timeline */}
                {estimatedWeeks > 0 && (
                  <div className="bg-gray-50 border border-gray-200 px-4 py-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">{t("calculator.sidebar.timeline")}</span>
                      <span
                        className="font-black text-lg text-black"
                        style={{ fontFamily: "Chivo, sans-serif" }}
                        data-testid="timeline-estimate"
                      >
                        ~{estimatedWeeks} {t("calculator.sidebar.weeks")}
                      </span>
                    </div>
                  </div>
                )}

                {/* Save message */}
                {saveMsg && (
                  <div className={`text-sm px-3 py-2 border ${saveMsg.includes("Kļūda") ? "text-red-600 bg-red-50 border-red-200" : "text-green-600 bg-green-50 border-green-200"}`}>
                    {saveMsg}
                  </div>
                )}

                {/* Action buttons */}
                <div className="space-y-2 pt-1">
                  <button
                    data-testid="save-project-btn"
                    onClick={saveProject}
                    disabled={itemsList.length === 0}
                    className="rmd-btn-secondary w-full justify-center text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Save size={14} /> {t("calculator.sidebar.saveProject")}
                    {!user && <span className="text-gray-400 font-normal">(login)</span>}
                  </button>

                  <button
                    data-testid="request-quote-btn"
                    onClick={() => { if (itemsList.length > 0) setShowQuote(true); }}
                    disabled={itemsList.length === 0}
                    className="rmd-btn-primary w-full justify-center text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FileText size={14} /> {t("calculator.sidebar.requestQuote")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black py-8 mt-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-white/30 text-xs">© {new Date().getFullYear()} RMD – Real Men Do · Cēsis, Latvija</p>
        </div>
      </footer>

      {/* Modals */}
      {showAuth && (
        <AuthModal onClose={() => setShowAuth(false)} onSuccess={() => {}} />
      )}
      {showQuote && (
        <QuoteModal
          items={itemsList}
          total={total}
          projectName={projectName || "Renovācijas projekts"}
          onClose={() => setShowQuote(false)}
        />
      )}
    </div>
  );
}
